import {Chart} from 'chart.js'
import {throttle} from 'lodash'
import moment from 'moment'

export type RangeSelectPlugin = {
  rangeSelect: RangeSelectOptions
}

export type ChartWithRangeSelectPlugin = {
  plugins: {
    rangeSelect: RangeSelectOptions
  }
}

export interface RangeSelectOnChange {
  startDate?: string
  endDate?: string
}

export type RangeSelectOptions = {
  onSelectionChanged?: (filteredDataSets: RangeSelectOnChange) => void
  fillColor?: string | CanvasGradient | CanvasPattern
  cursorColor?: string | CanvasGradient | CanvasPattern
  cursorWidth?: number
  state?: RangeSelectState
  minimalDateRange?: number
}

interface RangeSelectState {
  canvas: HTMLCanvasElement
  eventListeners: Array<{element: HTMLElement; type: string; handler: EventListener}>
  resizeObserver?: ResizeObserver
  isValidSelection?: boolean
}

interface ActiveSelection {
  x: number
  w: number
}

export const rangeSelectPlugin = {
  id: 'rangeSelect',
  beforeInit: function (chartInstance: Chart) {
    const opts = chartInstance.config.options
    if (hasRangeSelectPlugin(opts)) {
      const canvas = this.createOverlayCanvas(chartInstance, opts.plugins.rangeSelect)
      opts.plugins.rangeSelect = Object.assign({}, opts.plugins.rangeSelect, {
        state: {
          canvas: canvas,
          eventListeners: []
        }
      })

      if (chartInstance.canvas.parentElement) {
        chartInstance.canvas.parentElement.prepend(canvas)
      }
    }
  },
  updateOverlaySize(chart: Chart, overlay: HTMLCanvasElement) {
    const rect = chart.canvas.getBoundingClientRect()
    overlay.style.width = rect.width + 'px'
    overlay.style.height = rect.height + 'px'
    overlay.width = rect.width
    overlay.height = rect.height
  },
  resize(chartInstance: Chart) {
    const options = (chartInstance.config.options?.plugins as RangeSelectPlugin)?.rangeSelect
    if (options?.state) {
      // The ResizeObserver will handle the actual update
      this.updateOverlaySize(chartInstance, options.state.canvas)
    }
  },
  destroy(chartInstance: Chart) {
    const options = (chartInstance.config.options?.plugins as RangeSelectPlugin)?.rangeSelect
    if (options?.state) {
      options.state.eventListeners.forEach(({element, type, handler}) => {
        element.removeEventListener(type, handler)
      })

      if (options.state.resizeObserver) {
        options.state.resizeObserver.disconnect()
      }

      options.state.canvas.remove()
      delete options.state
    }
  },
  validateDateRange(
    startDate: string | undefined,
    endDate: string | undefined,
    minimalDateRange: number | undefined
  ): boolean {
    if (!minimalDateRange || !startDate || !endDate) {
      return true
    }

    const start = moment(startDate)
    const end = moment(endDate)
    const seconds = Math.abs(moment.duration(end.diff(start)).asSeconds())
    return seconds >= minimalDateRange
  },
  createOverlayCanvas(chart: Chart, options: RangeSelectOptions): HTMLCanvasElement {
    const overlay = this.createOverlayHtmlCanvasElement(chart)
    const ctx = overlay.getContext('2d')

    if (!options.state) {
      options.state = {
        canvas: overlay,
        eventListeners: [],
        resizeObserver: undefined
      }
    }
    const state = options.state

    let lastWidth = 0
    let lastHeight = 0

    const resizeObserver = new ResizeObserver((entries) => {
      // Use requestAnimationFrame to avoid ResizeObserver loop
      requestAnimationFrame(() => {
        for (const entry of entries) {
          if (entry.target === chart.canvas) {
            const rect = chart.canvas.getBoundingClientRect()

            if (rect.width !== lastWidth || rect.height !== lastHeight) {
              lastWidth = rect.width
              lastHeight = rect.height
              this.updateOverlaySize(chart, overlay)
            }
          }
        }
      })
    })

    resizeObserver.observe(chart.canvas)
    state.resizeObserver = resizeObserver

    const initialRect = chart.canvas.getBoundingClientRect()
    lastWidth = initialRect.width
    lastHeight = initialRect.height

    let selection: ActiveSelection = {x: 0, w: 0}
    let isDragging = false

    const getCanvasPosition = (event: PointerEvent | MouseEvent) => {
      const rect = chart.canvas.getBoundingClientRect()
      return {
        x: event.clientX - rect.left,
        y: event.clientY - rect.top
      }
    }

    const clearOverlay = () => {
      if (ctx) {
        const rect = chart.canvas.getBoundingClientRect()
        ctx.clearRect(0, 0, rect.width, rect.height)
      }
    }

    const pointerDownHandler = (evt: Event) => {
      const pointerEvt = evt as PointerEvent | MouseEvent
      const pos = getCanvasPosition(pointerEvt)
      selection.x = this.getXInChartArea(pos.x, chart)
      isDragging = true
    }

    const pointerLeaveHandler = () => {
      if (!isDragging) {
        clearOverlay()
      }
    }

    const pointerMoveHandler = throttle((evt: Event) => {
      if (!chart.canvas || !ctx) return

      clearOverlay()

      const pointerEvt = evt as PointerEvent | MouseEvent
      const pos = getCanvasPosition(pointerEvt)
      const currentX = this.getXInChartArea(pos.x, chart)

      if (isDragging) {
        selection.w = currentX - selection.x

        let isValid = true
        if (options?.minimalDateRange) {
          const tempResult = this.getDataSetDataInSelection(selection, chart, undefined)
          isValid = this.validateDateRange(
            tempResult.startDate,
            tempResult.endDate,
            options.minimalDateRange
          )
        }

        if (isValid) {
          ctx.fillStyle = options?.fillColor || '#00000044'
        } else {
          ctx.fillStyle = '#ff000044'
        }
        ctx.fillRect(
          selection.x,
          chart.chartArea.top,
          selection.w,
          chart.chartArea.bottom - chart.chartArea.top
        )
        state.isValidSelection = isValid
      } else {
        const cursorWidth = options?.cursorWidth || 1
        ctx.fillStyle = options?.cursorColor || '#00000088'
        ctx.fillRect(
          currentX,
          chart.chartArea.top,
          cursorWidth,
          chart.chartArea.bottom - chart.chartArea.top
        )
      }
    }, 16) // ~60fps throttling

    const pointerUpHandler = () => {
      if (isDragging) {
        const onSelectionChanged = options?.onSelectionChanged
        const minimalDateRange = options?.minimalDateRange
        if (onSelectionChanged) {
          onSelectionChanged(this.getDataSetDataInSelection(selection, chart, minimalDateRange))
        }
      }
      selection = {w: 0, x: 0}
      isDragging = false
      clearOverlay()
    }

    // Add event listeners and track them for cleanup
    const addEventListener = (element: HTMLElement, type: string, handler: EventListener) => {
      element.addEventListener(type, handler)
      state.eventListeners.push({element, type, handler})
    }

    addEventListener(chart.canvas, 'pointerdown', pointerDownHandler)
    addEventListener(chart.canvas, 'pointerleave', pointerLeaveHandler)
    addEventListener(chart.canvas, 'pointermove', pointerMoveHandler as EventListener)
    addEventListener(chart.canvas, 'pointerup', pointerUpHandler)

    return overlay
  },
  createOverlayHtmlCanvasElement(chartInstance: Chart): HTMLCanvasElement {
    const overlay = document.createElement('canvas')
    const rect = chartInstance.canvas.getBoundingClientRect()

    overlay.style.position = 'absolute'
    overlay.style.pointerEvents = 'none'
    overlay.style.top = '0'
    overlay.style.left = '0'

    overlay.width = rect.width
    overlay.height = rect.height
    overlay.style.width = rect.width + 'px'
    overlay.style.height = rect.height + 'px'

    return overlay
  },
  getXInChartArea(val: number, chartInstance: Chart) {
    return Math.min(Math.max(val, chartInstance.chartArea.left), chartInstance.chartArea.right)
  },
  getDataSetDataInSelection(
    selection: ActiveSelection,
    chartInstance: Chart,
    minimalDateRange: number | undefined
  ) {
    const result: {startDate?: string; endDate?: string} = {}
    const xMin = Math.min(selection.x, selection.x + selection.w)
    const xMax = Math.max(selection.x, selection.x + selection.w)

    if (chartInstance.data.datasets[0]) {
      const datasetMeta = chartInstance.getDatasetMeta(0)
      const dataInRange = datasetMeta.data.filter((data) => xMin <= data.x && xMax >= data.x)

      if (dataInRange.length > 0) {
        const firstElementIndex = datasetMeta.data.findIndex((data) => {
          const firstDataPoint = dataInRange[0]
          return firstDataPoint && data.x === firstDataPoint.x && data.y === firstDataPoint.y
        })

        const lastElementIndex = datasetMeta.data.findIndex((data) => {
          const lastDataPoint = dataInRange[dataInRange.length - 1]
          return lastDataPoint && data.x === lastDataPoint.x && data.y === lastDataPoint.y
        })

        if (firstElementIndex !== -1 && lastElementIndex !== -1 && chartInstance.data.labels) {
          const startLabel = chartInstance.data.labels[firstElementIndex] as string
          const endLabel = chartInstance.data.labels[lastElementIndex] as string

          if (this.validateDateRange(startLabel, endLabel, minimalDateRange)) {
            result.startDate = startLabel
            result.endDate = endLabel
          }
        }
      }
    }

    return result
  }
}

function hasRangeSelectPlugin(options: unknown): options is ChartWithRangeSelectPlugin {
  if (!options || typeof options !== 'object') return false

  const plugins = (options as {plugins?: unknown}).plugins
  return !!plugins && typeof plugins === 'object' && 'rangeSelect' in plugins
}
